<template>
  <div v-bem>
    <div v-bem:items>
      <Dimmable v-bem:item.language :dimmed="searchIsFocused">
        <a
          v-bem:itemTrigger
          title="Sprache"
          @click="$modal.show('languageModal')"
        >
          <Icon name="global" />
        </a>
        <modal
          name="languageModal"
          classes="meta__modal"
          width="100%"
          height="auto"
          :adaptive="true"
          :scrollable="true"
        >
          <div class="meta__modal-inner">
            <div class="meta__modal-actions-mobil">
              <button
                class="btn btn-icon-link icon-color-primary btn-link icon--arrow-left meta__modal-close pe-0 me-0"
                @click="$modal.hide('languageModal')"
              >
                {{ languageButtonclose }}
              </button>
            </div>
            <span class="meta__modal-title">
              {{ languageTitle }}
            </span>
            <span class="meta__modal-description">
              {{ languageDescription }}
            </span>
            <div class="meta__modal-actions-desktop">
              <button
                class="btn btn-icon-link icon-color-primary btn-link icon--arrow-left meta__modal-close"
                @click="$modal.hide('languageModal')"
              >
                {{ languageButtonclose }}
              </button>
              <a class="btn btn-outline-dark" :href="languageUrl">
                {{ languageButtontext }}
              </a>
            </div>
          </div>
          <div class="meta__modal-logo">
            <img :src="logoUrl" />
          </div>
        </modal>
      </Dimmable>
      <Dimmable v-if="isCartEnabled" v-bem:item.cart :dimmed="searchIsFocused">
        <a v-bem:itemTrigger :href="cartUrl">
          <Indicator :color="cartCount > 0 ? 'orange' : null">
            <Icon name="basket" />
          </Indicator>
        </a>
      </Dimmable>
      <div v-bem:item.search v-click-outside="handleClickOutsideSearch">
        <button
          v-bem:itemTrigger.search="{ isActive: activeWidget === 'search' }"
          :disabled="!$screen.lg"
          @click="toggleWidget('search')"
        >
          <Icon v-bem:searchIcon name="search" />
        </button>
        <form
          v-bem:searchForm="{ isOpen: activeWidget === 'search' }"
          @submit.prevent="handleSubmit"
        >
          <input
            ref="searchInput"
            v-bem:searchInput
            :value="searchTerm"
            type="text"
            :placeholder="t('accord.de durchsuchen')"
            @input="(e) => setSearchTerm(e.target.value)"
            @focus="setSearchInputIsFocused(true)"
            @blur="setSearchInputIsFocused(false)"
          />
        </form>
      </div>
      <Dimmable v-bem:item.account :dimmed="searchIsFocused">
        <button
          v-bem:itemTrigger="{ isActive: activeWidget === 'account' }"
          @click="toggleWidget('account')"
        >
          <Icon v-bem:accountIcon name="person" />
          <span v-bem:itemText="{ isActive: activeWidget === 'account' }">
            {{ isLoggedIn ? accountName : t('Login') }}
          </span>
        </button>
      </Dimmable>
    </div>
    <slide-up-down
      v-if="$screen.lg && isLoggedIn"
      :active="activeWidget === 'account'"
    >
      <AccountMenu />
    </slide-up-down>
    <transition name="fade">
      <ClosableOverlay
        v-if="activeWidget === 'account' && !$screen.lg && isLoggedIn"
      >
        <AccountMenu />
      </ClosableOverlay>
      <ClosableOverlay v-else-if="activeWidget === 'account' && !isLoggedIn">
        <Login />
      </ClosableOverlay>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import logo from '../assets/logo-plain.svg';
import AccountMenu from './AccountMenu.vue';
import ClosableOverlay from './ClosableOverlay.vue';
import Dimmable from './Dimmable.vue';
import Icon from './Icon.vue';
import Indicator from './Indicator.vue';
import Login from './Login.vue';

export default {
  name: 'Meta',
  components: {
    Dimmable,
    Indicator,
    Icon,
    Login,
    AccountMenu,
    ClosableOverlay,
  },
  computed: {
    ...mapState('pageHeader', [
      'activeWidget',
      'languageUrl',
      'languageTitle',
      'languageDescription',
      'languageButtontext',
      'languageButtonclose',
      'accountName',
      'accountLogoutUrl',
      'cartUrl',
      'isLoggedIn',
      'loginFormHtml',
      'doccheckFormHtml',
      'searchTerm',
      'searchIsFocused',
      'isCartEnabled',
      'cartCount',
    ]),
    logoUrl() {
      if (BUILD_TARGET === 'app-drupal') {
        return `/themes/particle/dist/${logo}`;
      }
      return logo;
    },
  },
  watch: {
    activeWidget(newValue) {
      if (newValue === 'search') {
        this.$refs.searchInput.focus();
      }
    },
  },
  methods: {
    handleClickOutsideSearch() {
      if (this.activeWidget === 'search') {
        this.toggleWidget('search');
      }
    },
    show() {
      this.$modal.show('languageModal');
    },
    hide() {
      this.$modal.hide('languageModal');
    },
    ...mapMutations('pageHeader', ['setSearchInputIsFocused', 'setSearchTerm']),
    ...mapActions('pageHeader', ['openWidget', 'closeWidget', 'toggleWidget']),
    handleSubmit() {
      window.location.href = `/search/${encodeURIComponent(this.searchTerm)}`;
    },
  },
  mount() {
    this.show();
  },
};
</script>

<style lang="scss">
@use '~tokens/sass/utils';
@use 'sass:map';

@include utils.vue-transition-fade;

.meta {
  background-color: #fff;
  color: map.get(utils.$v-colors, acc-black);

  @include utils.bs-media-breakpoint-up(lg) {
    color: map.get(utils.$v-colors, acc-black);
    border-color: transparentize(map.get(utils.$v-colors, acc-gray-080), 0.7);
    border-style: solid;
    border-width: 0 0 utils.f-convert-to-rem(1) utils.f-convert-to-rem(1);
    border-bottom-left-radius: utils.f-convert-to-rem(18);
  }

  // vue-modal specific rules
  .vm--overlay {
    background: none;
    @include utils.bs-media-breakpoint-up(lg) {
      background-color: map.get(utils.$v-colors, 'acc-black');
      opacity: 0.8;
    }
  }
  @at-root #{&}__modal {
    margin: auto;
    top: 60px !important;
    left: 0 !important;
    box-shadow: none;
    background-color: map.get(utils.$v-colors, 'acc-gray-020');
    @include utils.bs-media-breakpoint-down(lg) {
      min-height: 100vh;
    }
    @include utils.bs-media-breakpoint-up(lg) {
      background: map.get(utils.$v-colors, acc-white);
      border-radius: 0px 80px !important;
      max-width: 1145px;
      top: 150px !important;
    }
  }

  @at-root #{&}__modal-inner {
    padding: utils.f-rem-calc(20) utils.f-rem-calc(15);
    @include utils.bs-media-breakpoint-up(lg) {
      padding: utils.f-rem-calc(80) utils.f-rem-calc(130) 0 utils.f-rem-calc(80);
    }
  }

  @at-root #{&}__modal-actions-mobil {
    display: flex;
    justify-content: flex-end;
    margin-bottom: utils.f-rem-calc(50);
    @include utils.bs-media-breakpoint-up(lg) {
      display: none;
    }
  }

  @at-root #{&}__modal-actions-desktop {
    display: flex;
    margin-top: utils.f-rem-calc(40);
    @include utils.bs-media-breakpoint-down(lg) {
      margin-top: utils.f-rem-calc(25);
    }
    .meta__modal-close {
      @include utils.bs-media-breakpoint-down(lg) {
        display: none;
      }
    }
  }

  @at-root #{&}__modal-close {
    display: flex;
    align-items: center;
    color: map.get(utils.$v-colors, 'acc-black');
    text-decoration: none;
    padding-left: 0;
    margin-right: utils.f-rem-calc(15);
    &:hover {
      color: map.get(utils.$v-colors, 'acc-orange');
    }
    &:focus {
      background: none;
      border: 0;
      color: map.get(utils.$v-colors, 'acc-black');
      box-shadow: none;
    }
    &::before {
      margin-right: utils.f-rem-calc(10);
    }
  }
  @at-root #{&}__modal-title {
    color: map.get(utils.$v-colors, 'acc-black');
    display: block;
    @include utils.m-font('semibold');
    font-size: utils.f-rem-calc(24);
    line-height: utils.f-rem-calc(30);
    margin-bottom: utils.f-rem-calc(15);
    @include utils.bs-media-breakpoint-up(lg) {
      font-size: utils.f-rem-calc(48);
      line-height: utils.f-rem-calc(58);
      margin-bottom: utils.f-rem-calc(30);
    }
  }

  @at-root #{&}__modal-description {
    color: map.get(utils.$v-colors, 'acc-black');
    font-size: utils.f-rem-calc(16);
    line-height: utils.f-rem-calc(20);
    @include utils.bs-media-breakpoint-up(lg) {
      font-size: utils.f-rem-calc(20);
      line-height: utils.f-rem-calc(26);
    }
  }

  @at-root #{&}__modal-logo {
    display: none;
    @include utils.bs-media-breakpoint-up(lg) {
      display: flex;
      flex: 1;
      min-width: 100%;
      justify-content: flex-end;
      padding: 0 utils.f-rem-calc(70) utils.f-rem-calc(70) 0;
    }
  }

  // meta-navigation items

  @at-root #{&}__items {
    display: grid;
    grid-template-columns: 1fr utils.f-rem-calc(36) utils.f-convert-to-rem(36);
    grid-template-rows: utils.f-rem-calc(36) utils.f-rem-calc(44);
    gap: utils.f-rem-calc(16) 0;
    grid-auto-flow: row;
    // stylelint-disable-next-line declaration-colon-space-after
    grid-template-areas:
      'account cart language'
      'search search search';

    @include utils.bs-media-breakpoint-up(lg) {
      display: flex;
      height: utils.f-convert-to-rem(36);
      padding: 0 utils.f-rem-calc(7) 0 utils.f-rem-calc(4);
    }
  }

  @at-root #{&}__item {
    position: relative;
    display: flex;
    align-items: center;

    @at-root #{&}--account {
      grid-area: account;
      display: flex;
    }

    @at-root #{&}--cart {
      grid-area: cart;
    }

    @at-root #{&}--search {
      grid-area: search;
    }

    @at-root #{&}--language {
      grid-area: language;
    }
  }

  @at-root #{&}__item-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-width: utils.f-convert-to-rem(36);
    margin: 0;
    padding: 0;
    border: none;
    background-color: transparent;
    color: inherit;
    text-decoration: none;
    transition: all 320ms;
    cursor: pointer;

    @include utils.bs-media-breakpoint-up(lg) {
      min-width: 0;
      padding: 0 utils.f-rem-calc(6);
    }

    &:focus {
      outline: none;
    }

    @at-root {
      #{&}--is-active,
      &:hover {
        color: map.get(utils.$v-colors, acc-orange);
      }
    }

    @at-root #{&}--search {
      position: absolute;
      z-index: 1;
      left: utils.f-convert-to-rem(4);
      touch-action: none;

      @include utils.bs-media-breakpoint-up(lg) {
        position: static;
        touch-action: initial;
      }
    }
  }

  @at-root #{&}__item-text {
    display: block;
    margin-left: utils.f-convert-to-rem(12);
    transition: all 320ms;

    @at-root {
      #{&}--is-active,
      &:hover {
        color: map.get(utils.$v-colors, acc-black);
      }
    }
  }

  // The search and account icons are always orange on small screens.
  @at-root {
    #{&}__account-icon,
    #{&}__search-icon {
      @include utils.bs-media-breakpoint-down(lg) {
        color: map.get(utils.$v-colors, acc-orange);
      }
    }
  }

  @at-root #{&}__search-form {
    flex: 1;

    @include utils.bs-media-breakpoint-up(lg) {
      flex: initial;
      width: 0;
      overflow: hidden;
      transition: all 320ms;
    }

    @at-root #{&}--is-open {
      width: utils.f-convert-to-rem(160);
    }
  }

  @at-root #{&}__search-input {
    display: block;
    width: 100%;
    appearance: none;
    height: utils.f-rem-calc(44);
    padding: 0 utils.f-rem-calc(16) 0 utils.f-rem-calc(40);
    border: 1px solid map.get(utils.$v-colors, acc-gray-060);
    border-radius: utils.f-rem-calc(22);
    transition: all 320ms;

    @include utils.bs-media-breakpoint-up(lg) {
      height: utils.f-convert-to-rem(18);
      padding: 0;
      border: none;
      border-radius: 0;
      font-size: utils.f-rem-calc(14);
    }

    &::placeholder {
      color: map.get(utils.$v-colors, acc-black);
    }

    &:focus {
      outline: none;
    }
  }
}
</style>
